/**
 * BillingHero — the single anchor at the top of Settings → Subscription &
 * billing. It answers the two questions a founder has in the first second:
 * "what plan am I on?" and "is there anything I need to do?".
 *
 * It merges what used to be a separate plan-identity line and trial banner into
 * one block, so the page leads with one clear summary instead of competing
 * cards. The status colour rides a 3px LEFT RAIL on a white surface (brand spec
 * §2.5/§6.5 — tinted card backgrounds are forbidden). For a non-active state an
 * Owner on a priced tier gets a self-serve "Activate" button (Razorpay Checkout);
 * everyone else (non-owner, or a custom tier) falls back to the sales-led "Talk
 * to us" link. An active workspace shows a calm, action-free summary.
 */
import { useState } from "react";
import { MessageCircle, ArrowRight, ShieldCheck, Zap, CheckCircle2, Loader2, XCircle, Info } from "lucide-react";
import { cn, toast } from "@flint/ui";
import { PLAN_BY_CODE, formatPlanPrice, type PlanCode } from "@flint/api-contract";
import { usePermission } from "../../../lib/permissions.ts";
import { useSubscriptionCheckout } from "./razorpay.ts";
import { CancelSubscriptionDialog } from "./cancel-subscription-dialog.tsx";
import { trpc } from "../../../lib/trpc.ts";

export const TALK_URL =
  "https://wa.me/917012345678?text=Hi%2C+I%27d+like+to+activate+my+Flint+plan";

type Tone = "ok" | "info" | "warn" | "critical";

const RAIL: Record<Tone, string> = {
  ok: "border-l-ok",
  info: "border-l-sky-600",
  warn: "border-l-warn",
  critical: "border-l-bad",
};
const ICON_CHIP: Record<Tone, string> = {
  ok: "bg-ok-50 text-ok",
  info: "bg-sky-50 text-sky-700",
  warn: "bg-warn-50 text-warn",
  critical: "bg-bad-50 text-bad",
};
const ACCENT: Record<Tone, string> = {
  ok: "text-ok",
  info: "text-sky-700",
  warn: "text-warn",
  critical: "text-bad",
};

function formatDate(iso: string): string {
  return new Date(iso + "T00:00:00Z").toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export interface BillingHeroProps {
  /** Current plan code — drives the activation price + checkout. */
  plan: string;
  planName: string;
  status: string;
  trialEndsOn: string | null;
  trialDaysLeft: number | null;
  writeEnabled: boolean;
  /** ISO timestamp when the user scheduled cancel-at-period-end. Null = not scheduled. */
  cancelledAt: string | null;
  /** The "access ends on" date from Razorpay's `current_end`. */
  currentPeriodEndsOn: string | null;
}

export function BillingHero({
  plan,
  planName,
  status,
  trialEndsOn,
  trialDaysLeft,
  writeEnabled,
  cancelledAt,
  currentPeriodEndsOn,
}: BillingHeroProps) {
  const isActive = status === "active";
  const isTrialing = status === "trialing";
  const canManage = usePermission("billing.manage");
  const { activate, pendingPlan } = useSubscriptionCheckout();
  const [cancelOpen, setCancelOpen] = useState(false);

  const utils = trpc.useUtils();
  const cancel = trpc.billing.cancelSubscription.useMutation({
    onSuccess: async () => {
      await utils.billing.overview.invalidate();
      toast.success("Subscription cancelled", {
        description: "Your workspace stays fully usable until the end of the current period.",
      });
      setCancelOpen(false);
    },
    onError: (e: { message: string }) =>
      toast.error("Couldn't cancel", { description: e.message }),
  });
  const cancelScheduled = cancelledAt != null;
  // Users can schedule cancellation any time before Razorpay stops billing:
  // active paying subscribers, or trial-only tenants who don't want to convert.
  const canCancel = canManage && (isActive || isTrialing) && !cancelScheduled;

  const entry = PLAN_BY_CODE[plan];
  const price = entry ? formatPlanPrice(entry) : null;
  // Self-serve activation needs an Owner + a priced (self-serve) plan. Otherwise
  // (non-owner, or a custom tier like Design Partner) fall back to sales-led.
  const canSelfServe = canManage && price != null;
  const activating = pendingPlan === plan;
  const expired =
    status === "trial_expired" ||
    status === "soft_suspended" ||
    status === "hard_suspended" ||
    status === "canceled";

  const tone: Tone = isActive
    ? cancelScheduled
      ? "warn"
      : "ok"
    : expired || !writeEnabled
      ? "critical"
      : trialDaysLeft != null && trialDaysLeft <= 7
        ? "warn"
        : "info";

  // The effective app-stop date. Prefer Razorpay's cycle end
  // (`currentPeriodEndsOn`); fall back to `trialEndsOn` for a trial-scheduled
  // cancel where there's no Razorpay mandate yet. Null → we just say "soon".
  const effectiveEndDate = currentPeriodEndsOn ?? (isTrialing ? trialEndsOn : null);

  // One short status line under the plan name.
  const statusLine = isActive
    ? cancelScheduled
      ? effectiveEndDate
        ? `Cancelled — ends ${formatDate(effectiveEndDate)}`
        : "Cancelled — access ends soon"
      : "Active — you're all set"
    : status === "trialing"
      ? cancelScheduled
        ? effectiveEndDate
          ? `Trial · ends ${formatDate(effectiveEndDate)}`
          : "Trial · cancellation scheduled"
        : trialDaysLeft === 0
          ? "Free trial · ends today"
          : `Free trial${trialDaysLeft != null ? ` · ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left` : ""}`
      : expired
        ? "Read-only — activate to continue"
        : "Activation required";

  const Icon = cancelScheduled
    ? XCircle
    : isActive
      ? CheckCircle2
      : tone === "info"
        ? Zap
        : ShieldCheck;

  return (
    <div
      className={cn(
        "rounded-md border border-line border-l-[3px] bg-paper p-5 shadow-card",
        RAIL[tone],
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 gap-3">
          <span className={cn("inline-grid h-9 w-9 shrink-0 place-items-center rounded-md", ICON_CHIP[tone])}>
            <Icon size={17} strokeWidth={2} aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-base font-semibold text-ink">{planName} plan</p>
            <p className={cn("mt-0.5 text-sm font-medium", ACCENT[tone])}>{statusLine}</p>
            {isActive ? (
              cancelScheduled ? (
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-warn">
                  <Info size={13} aria-hidden /> After that, you can't access the workspace until you reactivate
                </p>
              ) : (
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted">
                  <ShieldCheck size={13} aria-hidden /> Manage your plan, usage and add-ons below
                </p>
              )
            ) : (
              <>
                <p className="mt-1.5 max-w-2xl text-sm text-ink-2">
                  {canSelfServe
                    ? "Activate now to keep your workspace live — pay securely with UPI AutoPay or card through Razorpay. It takes a minute."
                    : "Message us on WhatsApp and we'll confirm your plan and set everything up within the day."}
                </p>
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted">
                  <ShieldCheck size={13} aria-hidden /> Your data stays safe
                  {trialEndsOn ? ` · trial ends ${formatDate(trialEndsOn)}` : ""}
                </p>
              </>
            )}
          </div>
        </div>
        {!isActive && (
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            {canSelfServe ? (
              <>
                <button
                  type="button"
                  disabled={activating}
                  onClick={() => void activate(plan as PlanCode)}
                  className="inline-flex h-10 items-center gap-2 rounded-sm bg-ink px-4 text-sm font-semibold text-paper hover:bg-ink-2 disabled:opacity-60"
                >
                  {activating ? (
                    <Loader2 size={15} className="animate-spin" aria-hidden />
                  ) : (
                    <Zap size={15} strokeWidth={2} aria-hidden />
                  )}
                  {activating ? "Starting…" : `Activate · ${price}`}
                  {!activating && <ArrowRight size={15} aria-hidden />}
                </button>
                <a
                  href={TALK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted underline-offset-2 hover:underline"
                >
                  Questions? Talk to us
                </a>
              </>
            ) : (
              <a
                href={TALK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center gap-2 rounded-sm bg-ink px-4 text-sm font-semibold text-paper hover:bg-ink-2"
              >
                <MessageCircle size={15} strokeWidth={2} aria-hidden />
                Talk to us to activate
                <ArrowRight size={15} aria-hidden />
              </a>
            )}
          </div>
        )}
        {canCancel && (
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              className="inline-flex h-9 items-center gap-2 rounded-sm border border-line bg-paper px-3 text-xs font-semibold text-ink-2 hover:bg-bg"
            >
              Cancel plan
            </button>
          </div>
        )}
      </div>
      <CancelSubscriptionDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        currentPeriodEndsOn={currentPeriodEndsOn}
        pending={cancel.isPending}
        onConfirm={(reason) => cancel.mutate({ reason })}
      />
    </div>
  );
}
